import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlass, Sparkle, PaperPlaneTilt, ArrowRight,
  CheckCircle, X, Buildings, FileText,
} from "@phosphor-icons/react";

const SAMPLE_ORG = {
  website: "hollowcreek-coop.org",
  mission:
    "A member-owned cooperative bringing high-speed internet and digital skills training to farming communities in rural Montana.",
  keywords: ["rural broadband", "digital literacy", "rural development", "community connectivity", "workforce development"],
};

// Canned sample grant + proposal — all client-side, no backend or LLM cost.
// Demo lives entirely in this component so anonymous visitors can try the
// flow without authentication or burning Emergent LLM credits.
const SAMPLE_GRANT = {
  number: "USDA-RBD-25-1031",
  title: "Rural Broadband Infrastructure Expansion Grant",
  agency: "U.S. Department of Agriculture — Rural Development",
  amount: "Up to $5,000,000",
  due_in: "23 days",
  description:
    "Funds the deployment of high-capacity broadband infrastructure in unserved rural communities. Eligible applicants include municipalities, cooperatives, and nonprofit ISPs serving census-designated rural areas.",
  topics: ["rural broadband", "digital equity", "infrastructure"],
  contact: "broadband@usda.gov",
};

const SAMPLE_PROPOSAL = `# Rural Broadband Expansion in Northwest [REGION]

## Executive Summary
[ORG_NAME] respectfully requests $2,400,000 over 24 months to deploy a high-capacity fiber-optic network serving 1,840 unserved households across three contiguous rural census blocks. This investment will close a documented service gap, deliver symmetrical gigabit speeds to anchor institutions, and create 14 sustained construction and operations jobs.

## Statement of Need
According to the FCC National Broadband Map (2026 release), 71% of households in our service territory lack access to broadband meeting the 100/20 Mbps benchmark. Our needs assessment of 412 residents confirmed (1) zero providers offering 100 Mbps service in 8 of the 11 affected ZIP codes, (2) median monthly internet spend $97 — 38% above state median, (3) a 19-percentage-point telehealth-utilization gap.

## Project Plan
**Phase 1 (Months 1-6)** — Permitting, pole-attachment agreements, environmental review.
**Phase 2 (Months 7-18)** — 142 miles of aerial and underground fiber-to-the-premises construction.
**Phase 3 (Months 19-24)** — Customer activation, anchor-institution lit-up, low-income enrollment outreach.

## Evaluation Plan
Quarterly reporting on (a) homes passed, (b) take-rate, (c) median speeds delivered, (d) ACP-enrollment among newly served low-income households. Independent third-party verification by [EVALUATOR_NAME] at months 12 and 24.

## Budget Narrative
$1,820,000 construction · $310,000 equipment · $190,000 personnel · $80,000 compliance and reporting. Detailed line items provided in Appendix B.

## Organizational Capacity
[ORG_NAME] has operated as a cooperative ISP since [YEAR_FOUNDED], currently serving [CURRENT_SUBS] subscribers. Our team includes three OSP engineers with combined 47 years of fiber-network experience.

## Sustainability
Post-deployment operations funded by member-cooperative subscription revenue at projected $59/month average. Break-even at 56% take-rate; conservative projections show 71% take-rate by month 30.

---

*Submitted by [ORG_NAME] · Primary contact: [APPLICANT_NAME], [APPLICANT_TITLE]*`;

// "Stream" the canned proposal one chunk at a time to simulate Claude drafting.
function useStreamedDraft(active) {
  const [text, setText] = useState("");
  const timer = useRef(null);
  useEffect(() => {
    if (!active) {
      setText("");
      if (timer.current) clearInterval(timer.current);
      return;
    }
    let i = 0;
    const chunkSize = 38; // characters per tick → ~1.6s total to finish
    timer.current = setInterval(() => {
      i += chunkSize;
      setText(SAMPLE_PROPOSAL.slice(0, i));
      if (i >= SAMPLE_PROPOSAL.length) clearInterval(timer.current);
    }, 30);
    return () => clearInterval(timer.current);
  }, [active]);
  return text;
}

export default function TryItOutDemo({ onClose }) {
  // step: "assist" | "search" | "draft" | "sent" | "signup"
  const [step, setStep] = useState("assist");
  const [searchInput, setSearchInput] = useState("rural broadband");
  const draftText = useStreamedDraft(step === "draft" || step === "sent" || step === "signup");
  const drafting = step === "draft" && draftText.length < SAMPLE_PROPOSAL.length;

  // ESC closes the demo
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const headerStep = ["assist", "search", "draft", "sent"].indexOf(step) + 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
      data-testid="try-demo-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border-2 border-zinc-900 shadow-[8px_8px_0_0_rgba(24,24,27,1)] w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-zinc-200 px-5 py-3 flex items-center justify-between bg-zinc-50">
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 bg-blue-700 flex items-center justify-center">
              <Buildings size={14} className="text-white" weight="bold" />
            </div>
            <div>
              <div className="text-xs font-bold tracking-tight">GrantFlow — interactive demo</div>
              <div className="text-[10px] text-zinc-500 font-mono">No account needed · No signup required</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="try-demo-close"
            className="p-1.5 hover:bg-zinc-200 text-zinc-600"
            aria-label="Close demo"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Stepper */}
        {step !== "signup" && (
          <div className="border-b border-zinc-200 px-5 py-3 flex items-center gap-4 text-xs font-mono uppercase tracking-wider">
            {["Assist", "Search", "Draft", "Send"].map((label, i) => {
              const idx = i + 1;
              const active = headerStep === idx;
              const done = headerStep > idx;
              return (
                <div key={label} className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-6 h-6 border ${
                    done ? "bg-emerald-600 border-emerald-600 text-white"
                    : active ? "bg-blue-700 border-blue-700 text-white"
                    : "bg-white border-zinc-300 text-zinc-400"
                  }`}>{done ? "✓" : idx}</span>
                  <span className={done || active ? "text-zinc-900 font-bold" : "text-zinc-400"}>{label}</span>
                  {i < 3 && <span className="text-zinc-300 mx-1">→</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {step === "assist" && (
            <AssistStep
              onPickKeyword={(kw) => {
                setSearchInput(kw);
                setStep("search");
              }}
              onSkip={() => setStep("search")}
            />
          )}
          {step === "search" && (
            <SearchStep
              value={searchInput}
              setValue={setSearchInput}
              onPick={() => setStep("draft")}
            />
          )}
          {(step === "draft" || step === "sent") && (
            <DraftStep
              text={draftText}
              drafting={drafting}
              onSend={() => setStep("sent")}
              sent={step === "sent"}
              onContinue={() => setStep("signup")}
            />
          )}
          {step === "signup" && <SignupStep />}
        </div>
      </div>
    </div>
  );
}

function AssistStep({ onPickKeyword, onSkip }) {
  // "analyzing" simulates the Claude website analysis without any LLM cost.
  const [phase, setPhase] = useState("input"); // input | analyzing | done
  useEffect(() => {
    if (phase !== "analyzing") return;
    const t = setTimeout(() => setPhase("done"), 1400);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div className="p-6 sm:p-8" data-testid="try-demo-assist">
      <div className="gf-overline">Step 1 of 4</div>
      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2">
        Tell us who you are — AI finds your search terms.
      </h3>
      <p className="text-zinc-600 text-sm mt-2 max-w-xl">
        Paste your organization&apos;s website (or a few keywords) and GrantFlow&apos;s AI suggests
        the exact terms funders use. For the demo we&apos;ll analyze a sample co-op.
      </p>

      <div className="mt-6 flex gap-2 items-stretch">
        <div className="flex-1 flex items-stretch border-2 border-zinc-900">
          <div className="bg-zinc-900 flex items-center px-3">
            <Buildings size={18} className="text-white" />
          </div>
          <input
            type="text"
            value={SAMPLE_ORG.website}
            readOnly
            className="flex-1 px-3 py-3 font-mono text-sm outline-none bg-white"
            data-testid="try-demo-assist-input"
          />
        </div>
        <button
          onClick={() => setPhase("analyzing")}
          disabled={phase !== "input"}
          data-testid="try-demo-analyze-btn"
          className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-widest px-6 text-xs disabled:opacity-50"
        >
          {phase === "input" ? "Analyze" : phase === "analyzing" ? "Analyzing…" : "Done"}
        </button>
      </div>

      {phase === "analyzing" && (
        <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5" data-testid="try-demo-assist-analyzing">
          <span className="inline-block h-2 w-2 bg-blue-700 animate-pulse rounded-full" />
          Reading site · extracting mission · mapping funder vocabulary…
        </div>
      )}

      {phase === "done" && (
        <div className="mt-5 animate-in fade-in duration-300" data-testid="try-demo-assist-result">
          <p className="text-sm text-zinc-700 max-w-xl">
            <b>What we understood:</b> {SAMPLE_ORG.mission}
          </p>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-4">
            Suggested search terms — click one to search
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {SAMPLE_ORG.keywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => onPickKeyword(kw)}
                data-testid={`try-demo-assist-kw-${kw.replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 border border-zinc-300 bg-zinc-50 hover:border-blue-700 hover:text-blue-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-zinc-900 transition-colors"
              >
                <MagnifyingGlass size={11} /> {kw}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400 font-mono mt-3">
            In the real app you can also save any of these as daily email alerts.
          </p>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={onSkip}
          data-testid="try-demo-assist-skip"
          className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900"
        >
          Skip — search manually →
        </button>
      </div>
    </div>
  );
}

function SearchStep({ value, setValue, onPick }) {
  return (
    <div className="p-6 sm:p-8" data-testid="try-demo-search">
      <div className="gf-overline">Step 2 of 4</div>
      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2">
        Search the live federal grants database.
      </h3>
      <p className="text-zinc-600 text-sm mt-2 max-w-xl">
        In the real app this searches Grants.gov in real time. For the demo
        we&apos;ll use this sample search.
      </p>

      <div className="mt-6 flex gap-2 items-stretch border-2 border-zinc-900">
        <div className="bg-zinc-900 flex items-center px-3">
          <MagnifyingGlass size={18} className="text-white" />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1 px-3 py-3 font-mono text-sm outline-none"
          data-testid="try-demo-search-input"
        />
      </div>

      <div className="mt-6 border border-zinc-200 hover:border-blue-700 transition-colors p-5 cursor-pointer group"
           onClick={onPick}
           data-testid="try-demo-grant-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{SAMPLE_GRANT.number}</div>
            <h4 className="font-display font-bold text-lg tracking-tight mt-1">{SAMPLE_GRANT.title}</h4>
            <div className="text-xs font-mono text-zinc-500 mt-1">{SAMPLE_GRANT.agency}</div>
            <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{SAMPLE_GRANT.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {SAMPLE_GRANT.topics.map((t) => (
                <span key={t} className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 font-mono uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Award</div>
            <div className="font-display font-black text-lg text-blue-700">{SAMPLE_GRANT.amount}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mt-3">Closes in</div>
            <div className="font-bold text-sm">{SAMPLE_GRANT.due_in}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onPick}
          data-testid="try-demo-pick-grant-btn"
          className="group bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2 text-xs"
        >
          Draft a proposal for this <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" weight="bold" />
        </button>
      </div>
    </div>
  );
}

function DraftStep({ text, drafting, onSend, sent, onContinue }) {
  return (
    <div className="p-6 sm:p-8" data-testid="try-demo-draft">
      <div className="gf-overline">Step {sent ? 4 : 3} of 4</div>
      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2">
        {sent ? "Sent. Receipt logged." : "Claude 4.5 is drafting your proposal."}
      </h3>
      <p className="text-zinc-600 text-sm mt-2 max-w-xl">
        {sent
          ? "In the real app, this gets emailed directly to the agency contact with a DKIM-verified receipt. Tracking, outcome reporting, and follow-ups are all handled automatically."
          : "Every section — needs, plan, evaluation, budget narrative — generated in seconds. You review, polish, and submit."}
      </p>

      {drafting && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5">
          <span className="inline-block h-2 w-2 bg-blue-700 animate-pulse rounded-full" />
          Drafting · {Math.min(SAMPLE_PROPOSAL.length, text.length)} / {SAMPLE_PROPOSAL.length} chars
        </div>
      )}
      {sent && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5">
          <CheckCircle size={14} weight="fill" /> Submitted to {SAMPLE_GRANT.contact} · receipt #demo-r{Math.floor(Math.random() * 9000) + 1000}
        </div>
      )}

      <div className="mt-5 border border-zinc-200 bg-zinc-50 max-h-[44vh] overflow-y-auto">
        <pre
          data-testid="try-demo-draft-text"
          className="p-5 text-xs font-mono whitespace-pre-wrap leading-relaxed text-zinc-900"
        >{text}{drafting && <span className="inline-block w-1.5 h-3.5 bg-blue-700 ml-0.5 animate-pulse align-text-bottom" />}</pre>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
        {!sent && (
          <button
            onClick={onSend}
            disabled={drafting}
            data-testid="try-demo-send-btn"
            className="group bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center justify-center gap-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <PaperPlaneTilt size={14} weight="bold" /> {drafting ? "Waiting for draft…" : "Send to agency"}
          </button>
        )}
        {sent && (
          <button
            onClick={onContinue}
            data-testid="try-demo-continue-btn"
            className="group bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center justify-center gap-2 text-xs"
          >
            Do this for real <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function SignupStep() {
  return (
    <div className="p-6 sm:p-10 text-center" data-testid="try-demo-signup">
      <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5">
        <CheckCircle size={12} weight="fill" /> Demo complete
      </div>
      <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-5 leading-tight max-w-2xl mx-auto">
        That&apos;s the whole flow. <span className="text-blue-700">Now do it with real grants.</span>
      </h3>
      <p className="text-zinc-600 mt-4 max-w-xl mx-auto">
        Create a free account to search the live Grants.gov database, generate AI proposals against your own org details, and submit them to real agency contacts.
      </p>

      <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
        {[
          { icon: Sparkle, label: "AI turns your website into search terms + alerts" },
          { icon: MagnifyingGlass, label: "2,000+ live federal grants" },
          { icon: FileText, label: "AI drafts in under 60 sec · Free trial, no card" },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="border border-zinc-200 p-3 flex items-start gap-2">
            <Icon size={16} weight="bold" className="text-blue-700 mt-0.5 shrink-0" />
            <span className="text-xs text-zinc-700 leading-relaxed">{label}</span>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/register"
          data-testid="try-demo-signup-cta"
          className="group bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center justify-center gap-2 text-sm"
        >
          Create my free account
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" weight="bold" />
        </Link>
        <Link
          to="/login"
          data-testid="try-demo-signin-cta"
          className="border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center justify-center text-sm transition-colors"
        >
          I already have an account
        </Link>
      </div>
    </div>
  );
}
